import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { ArrowLeft } from "lucide-react";
import ResultCard from "./verify/ResultCard";

const PORTAL_BG = "https://static.prod-images.emergentagent.com/jobs/d0292276-e84a-42f6-aad0-4f23c6fd8a65/images/55a57bc52f06288ab5cfc331366ab176d1241dd60b6402e36416b1dbe8fa5282.png";

const PAGE_STYLE = {
  backgroundImage: `linear-gradient(rgba(10,10,12,0.92), rgba(10,10,12,0.96)), url(${PORTAL_BG})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
};

export default function Verify() {
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [input, setInput] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const lookup = useCallback(async (codeValue) => {
    const v = (codeValue || "").trim().toUpperCase();
    if (!v) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.get(`/codes/${encodeURIComponent(v)}/authenticity`);
      setResult(data);
    } catch (e) {
      setError("Could not verify right now. Try again later.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const c = (params.get("code") || "").trim().toUpperCase();
    if (c) {
      setInput(c);
      lookup(c);
    }
  }, [params, lookup]);

  const submit = (e) => {
    e?.preventDefault?.();
    lookup(input);
  };

  return (
    <div className="min-h-screen w-full grain" style={PAGE_STYLE} data-testid="verify-page">
      <header className="px-6 sm:px-10 py-6 flex items-center justify-between">
        <a href="/" className="flex items-center gap-3" data-testid="verify-brand-link">
          <img src={settings.logo_url} alt={settings.brand_name} className="w-9 h-9 object-contain" style={{ mixBlendMode: "lighten" }} />
          <span className="font-display text-lg tracking-wide">{settings.brand_name}</span>
        </a>
        <button onClick={() => navigate("/")} className="overline text-[var(--vault-text-muted)] hover:text-white transition-colors inline-flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Portal
        </button>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-8 sm:py-14">
        <div className="text-center mb-10 fade-up">
          <p className="overline text-[var(--vault-text-muted)] mb-3">Public Authenticity Check</p>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight">Verify a certificate</h1>
          <p className="text-[var(--vault-text-muted)] mt-4 max-w-md mx-auto">
            Enter the code printed on the bottle to confirm it was issued by {settings.brand_name} and view its public record.
          </p>
        </div>

        <form onSubmit={submit} className="flex gap-2 max-w-md mx-auto fade-up delay-1" data-testid="verify-form">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value.toUpperCase().slice(0, 32))}
            placeholder="ABC123"
            className="flex-1 px-4 py-3 rounded-md font-mono-vault tracking-widest text-center outline-none"
            style={{ background: "var(--vault-surface)", border: "1px solid var(--vault-border)" }}
            data-testid="verify-input"
            autoComplete="off"
          />
          <button type="submit" disabled={loading || !input.trim()} className="btn-gold px-5 py-3 rounded-md text-sm" data-testid="verify-submit">
            {loading ? <span className="spin" /> : "Verify"}
          </button>
        </form>

        {error && (
          <p className="text-center mt-6 text-sm" style={{ color: "#FCA5A5" }} data-testid="verify-error">{error}</p>
        )}

        {result && <ResultCard result={result} />}
      </main>

      <p className="text-center overline text-[var(--vault-text-faint)] pb-8 mt-4">
        Public proof · Tamper-evident · {settings.brand_name}
      </p>
    </div>
  );
}
